import { useCallback, useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import i18next from "i18next";

import { addNotification } from "notificationStore.js";

import { resetPendingSaves } from "actions/pendingSaveActions.js";

import {
  isLoadingSelector as isOrderDetailsLoadingSelector,
  hasRefreshFailedSelector
} from "selectors/orderDetailsSelector.js";
import { pendingSaveCountSelector } from "selectors/pendingSaveSelector.js";

// A save that never reports back (e.g. its saga was cancelled before
// dispatching success/failure) would otherwise keep checkout blocked until a
// page reload. Any change in the in-flight count restarts this window.
export const PENDING_SAVE_TIMEOUT_MS = 30000;

// Submitting an order (Pay Now or regular Submit Order) waits for any
// in-flight quantity/price save to finish, then re-fetches the order's current
// pricing, and only then calls `onProceed` — instead of sending whatever total
// happened to be loaded when the dialog first opened.
//
// `createRefreshAction` must be memoized and should return an order-details
// request carrying `{ metadata: { preserveSelections: true } }`, so the
// refresh keeps the user's shipping address and minimum-order acknowledgement.
export const useRefreshThenSubmit = (createRefreshAction, onProceed) => {
  const dispatch = useDispatch();
  const pendingSaveCount = useSelector(pendingSaveCountSelector);
  const isOrderDetailsLoading = useSelector(isOrderDetailsLoadingSelector);
  const hasRefreshFailed = useSelector(hasRefreshFailedSelector);
  // null = not submitting; otherwise the argument (payNow) the submission
  // will be proceeded with.
  const [pendingSubmitArg, setPendingSubmitArg] = useState(null);
  const hasStartedRefreshRef = useRef(false);
  const hasSeenRefreshLoadingRef = useRef(false);

  const cancelSubmit = useCallback(() => {
    hasStartedRefreshRef.current = false;
    hasSeenRefreshLoadingRef.current = false;
    setPendingSubmitArg(null);
  }, []);

  const requestSubmit = useCallback((arg = false) => {
    hasStartedRefreshRef.current = false;
    hasSeenRefreshLoadingRef.current = false;
    setPendingSubmitArg(arg);
  }, []);

  useEffect(() => {
    if (pendingSaveCount === 0) return undefined;

    const timeoutId = setTimeout(() => {
      dispatch(resetPendingSaves());
    }, PENDING_SAVE_TIMEOUT_MS);

    return () => clearTimeout(timeoutId);
  }, [pendingSaveCount, dispatch]);

  useEffect(() => {
    if (pendingSubmitArg === null) return;

    // Phase 1: don't even ask for fresh pricing while a save is still in
    // flight — that fetch would just come back with the same stale data.
    if (pendingSaveCount > 0) return;

    // Phase 2: kick off (once) and wait for the pricing refresh itself.
    if (!hasStartedRefreshRef.current) {
      hasStartedRefreshRef.current = true;
      dispatch(createRefreshAction());

      return;
    }

    if (isOrderDetailsLoading) {
      hasSeenRefreshLoadingRef.current = true;

      return;
    }

    if (!hasSeenRefreshLoadingRef.current) return;

    const submitArg = pendingSubmitArg;
    cancelSubmit();

    if (hasRefreshFailed) {
      addNotification({
        type: "danger",
        message: i18next.t("order.errors.refreshFailed")
      });

      return;
    }

    onProceed(submitArg);
  }, [
    pendingSubmitArg,
    pendingSaveCount,
    isOrderDetailsLoading,
    hasRefreshFailed,
    dispatch,
    createRefreshAction,
    onProceed,
    cancelSubmit
  ]);

  return { requestSubmit, cancelSubmit };
};
